<template>
  <div class="supply-agreement-templates">
    <div class="templates-top-bar">
      <div class="templates-top-bar__title">Шаблоны договоров поставок по поставщикам</div>
      <CustomButton
        class="header__create-btn"
        @click="openDialog()"
      >
        <span>Добавить шаблон</span>
        <q-icon name="add" class="header__create-btn-icon" size="16px" />
      </CustomButton>
    </div>

    <div class="templates-hint">
      <q-icon name="info" color="info" size="18px" />
      <span>Если у поставщика есть активный шаблон — он будет использоваться при генерации договора. Иначе используется общий шаблон.</span>
    </div>

    <CustomTable 
      title="Шаблоны" 
      :rowsData="rowsData" 
      :columns="columns" 
      :loading="isLoading"
      :small="true"
      :show-search="true"
      :custom-body="true"
      :sticky-header="false"
    >
      <template #customBody="props">
        <q-tr>
          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.supply_agreement_template_id }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.provider_name || '-' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.template_name || '-' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            {{ props.row.payment_order_name || props.row.payment_order_id || '-' }}
          </custom-table-cell>

          <custom-table-cell align="left" vertical-align="center">
            <span class="file-name-link" @click="downloadTemplate(props.row)">
              <q-icon name="description" size="16px" color="primary" />
              {{ props.row.file_name || '-' }}
            </span>
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <q-toggle
              :model-value="props.row.is_active"
              color="positive"
              @update:model-value="(val) => toggleActive(props.row, val)"
              :loading="toggleLoading[props.row.supply_agreement_template_id]"
            />
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            {{ props.row.created_at ? formatDateTime(props.row.created_at) : '-' }}
          </custom-table-cell>

          <custom-table-cell align="center" vertical-align="center">
            <CustomDropdown
              :loading="actionLoading[props.row.supply_agreement_template_id]"
              @click="(action) => handleAction(action, props.row)"
              :options="getActionsDropdown(props.row)"
              title="Действие"
            />
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <!-- Диалог для добавления/редактирования шаблона -->
    <q-dialog v-model="dialog" persistent>
      <q-card class="template-modal">
        <q-card-section class="template-modal__header">
          <div class="template-modal__title">{{ editing ? 'Редактировать шаблон' : 'Добавить шаблон' }}</div>
          <q-btn icon="close" flat round dense v-close-popup class="template-modal__close" />
        </q-card-section>

        <q-card-section class="template-modal__content">
          <q-form @submit.prevent="save" class="template-form">
            <div class="template-form__section">
              <div class="template-form__item">
                <CustomSelect
                  label="Поставщик"
                  :options="formattedProviders"
                  option-label-key="provider_name"
                  option-value-key="provider_id"
                  :value="form.provider_id"
                  @change="(value) => form.provider_id = value"
                  :loading="isLoadingProviders"
                  :clearable="true"
                />
              </div>

              <div class="template-form__item">
                <q-input
                  v-model="form.template_name"
                  label="Название шаблона"
                  outlined
                  dense
                  :rules="[val => !!val || 'Обязательное поле']"
                />
              </div>

              <div class="template-form__item">
                <CustomSelect
                  label="Порядок оплат"
                  :options="paymentOrderOptions"
                  option-label-key="payment_order_name"
                  option-value-key="payment_order_id"
                  :value="form.payment_order_id"
                  @change="(value) => form.payment_order_id = value"
                  :clearable="false"
                />
              </div>

              <div class="template-form__item">
                <q-file
                  v-model="form.file"
                  label="Файл шаблона (.docx)"
                  outlined
                  dense
                  accept=".docx"
                  :rules="editing ? [] : [val => !!val || 'Файл обязателен']"
                >
                  <template #prepend>
                    <q-icon name="attach_file" />
                  </template>
                </q-file>
                <div v-if="editing && !form.file" class="template-form__file-hint">
                  <q-icon name="info" size="14px" color="grey-7" />
                  Текущий файл: {{ editing.file_name }}. Загрузите новый файл, чтобы заменить.
                </div>
              </div>

              <div class="template-form__item">
                <q-checkbox
                  v-model="form.is_active"
                  label="Активен (будет использоваться для генерации договора)"
                  color="primary"
                />
              </div>
            </div>

            <div class="template-modal__footer">
              <CustomButton
                @click="dialog = false"
                :disabled="isSaving"
                outlined
              >
                Отмена
              </CustomButton>
              <CustomButton
                type="submit"
                :loading="isSaving"
              >
                {{ editing ? 'Сохранить' : 'Создать' }}
              </CustomButton>
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог подтверждения удаления -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomSelect from "@/components/form/CustomSelect";
import CustomDropdown from "@/components/common/CustomDropdown";
import {
  getSupplyAgreementTemplates,
  deleteSupplyAgreementTemplate,
  supplyAgreementTemplateColumns
} from "@/views/ContractorAgreementList/services/templateServices";
import { supplyAgreementTemplateAPI } from "@/views/ContractorAgreementList/services/templateApi";
import { getProviders } from "@/views/ContractorAgreementList/services";

export default {
  name: "SupplyAgreementTemplates",
  components: {
    CustomSelect,
    CustomDropdown,
    CustomButton,
    CustomTable,
    CustomTableCell,
  },
  data() {
    return {
      rowsData: [],
      isLoading: false,
      dialog: false,
      editing: null,
      form: {
        provider_id: null,
        template_name: '',
        payment_order_id: 1,
        file: null,
        is_active: true,
      },
      isSaving: false,
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
      columns: supplyAgreementTemplateColumns,
      providers: [],
      isLoadingProviders: false,
      actionLoading: {},
      toggleLoading: {},
    };
  },
  computed: {
    formattedProviders() {
      return this.providers.map(item => ({
        ...item,
        provider_name: item.provider_id ? `${item.provider_id} / ${item.provider_name || ''}` : item.provider_name
      }));
    },
    paymentOrderOptions() {
      return [
        { payment_order_id: 2, payment_order_name: 'Оплата по факту' },
        { payment_order_id: 1, payment_order_name: 'Оплата по умолчанию' },
      ]
    },
  },
  methods: {
    async loadData() {
      this.isLoading = true;
      const res = await getSupplyAgreementTemplates();
      this.isLoading = false;
      if (!res) return;
      this.rowsData = res;
    },
    async loadDataSilently() {
      const res = await getSupplyAgreementTemplates();
      if (res) {
        this.rowsData = res;
      }
    },
    async loadProviders() {
      this.isLoadingProviders = true;
      const res = await getProviders();
      if (res) {
        this.providers = res;
      }
      this.isLoadingProviders = false;
    },
    getActionsDropdown(row) {
      const canDelete = row?.can_delete === true;
      const agreementCnt = Number(row?.agreement_cnt || 0);
      const isUsedInAgreements = agreementCnt > 0;
      const usedHint = `Недоступно: шаблон использован в договорах (${agreementCnt}). Можно только менять активность.`;

      return [
        {
          label: 'Редактировать',
          action: 'edit',
          icon: 'edit',
          disabled: isUsedInAgreements,
          hint: isUsedInAgreements ? usedHint : '',
        },
        {
          label: 'Скачать шаблон',
          action: 'download',
          icon: 'download',
          disabled: false,
          hint: '',
        },
        {
          label: 'Удалить',
          action: 'delete',
          icon: 'delete',
          color: 'negative',
          disabled: !canDelete || isUsedInAgreements,
          hint: (!canDelete || isUsedInAgreements) ? usedHint : '',
        },
      ];
    },
    async handleAction(action, row) {
      if (action?.disabled) return;

      const id = row.supply_agreement_template_id;
      
      switch (action.action) {
        case 'edit':
          this.openDialog(row);
          break;
        case 'download':
          this.actionLoading[id] = true;
          try {
            await this.downloadTemplate(row);
          } finally {
            this.actionLoading[id] = false;
          }
          break;
        case 'delete':
          this.confirmDelete(row);
          break;
      }
    },
    async downloadTemplate(row) {
      try {
        const response = await supplyAgreementTemplateAPI.download(row.supply_agreement_template_id);
        const { saveAs } = await import('file-saver');
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });
        saveAs(blob, row.file_name || 'template.docx');
        this.showSucceedSnackbar('Шаблон скачан');
      } catch (e) {
        console.error('Error downloading template:', e);
        this.showErrorSnackbar('Ошибка при скачивании шаблона');
      }
    },
    async toggleActive(row, newValue) {
      const id = row.supply_agreement_template_id;
      this.toggleLoading[id] = true;
      try {
        await supplyAgreementTemplateAPI.toggleActive(id, newValue);
        this.showSucceedSnackbar(newValue ? 'Шаблон активирован' : 'Шаблон деактивирован');
        await this.loadDataSilently();
      } catch (e) {
        console.error('Error toggling template:', e);
        this.showErrorSnackbar('Ошибка при изменении статуса шаблона');
      } finally {
        this.toggleLoading[id] = false;
      }
    },
    async openDialog(row = null) {
      this.editing = row;
      this.dialog = true;

      await this.loadProviders();

      if (row) {
        this.form = {
          provider_id: row.provider_id,
          template_name: row.template_name || '',
          payment_order_id: row.payment_order_id || 1,
          file: null,
          is_active: row.is_active ?? true,
        };
      } else {
        this.form = {
          provider_id: null,
          template_name: '',
          payment_order_id: 1,
          file: null,
          is_active: true,
        };
      }
    },
    async save() {
      if (!this.form.provider_id) {
        this.showErrorSnackbar('Выберите поставщика');
        return;
      }
      if (!this.form.template_name) {
        this.showErrorSnackbar('Укажите название шаблона');
        return;
      }
      if (!this.editing && !this.form.file) {
        this.showErrorSnackbar('Загрузите файл шаблона');
        return;
      }

      this.isSaving = true;

      const formData = new FormData();
      if (this.editing) {
        formData.append('supply_agreement_template_id', this.editing.supply_agreement_template_id);
      }
      formData.append('provider_id', this.form.provider_id);
      formData.append('template_name', this.form.template_name);
      formData.append('payment_order_id', this.form.payment_order_id || 1);
      formData.append('is_active', this.form.is_active);
      
      if (this.form.file) {
        formData.append('file', this.form.file);
      }

      try {
        const res = await supplyAgreementTemplateAPI.save(formData);
        this.isSaving = false;

        if (res) {
          this.showSucceedSnackbar(this.editing ? 'Шаблон обновлен' : 'Шаблон добавлен');
          this.dialog = false;
          await this.loadDataSilently();
        }
      } catch (e) {
        this.isSaving = false;
        console.error('Error saving template:', e);
        this.showErrorSnackbar('Ошибка при сохранении шаблона');
      }
    },
    confirmDelete(row) {
      this.confirmDialog = {
        show: true,
        title: 'Подтверждение',
        message: `Вы уверены, что хотите удалить шаблон "${row.template_name}" (ID ${row.supply_agreement_template_id})?`,
        onConfirm: async () => {
          await this.deleteHandler(row.supply_agreement_template_id);
        }
      };
    },
    async deleteHandler(supply_agreement_template_id) {
      const res = await deleteSupplyAgreementTemplate(supply_agreement_template_id);
      if (res) {
        this.showSucceedSnackbar('Шаблон удален');
        await this.loadDataSilently();
      }
    },
    async onConfirmDelete() {
      this.confirmDialog.show = false;
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        return date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
      } catch (e) {
        return dateStr;
      }
    },
  },
  mounted() {
    this.loadData();
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
};
</script>

<style scoped lang="scss">
.supply-agreement-templates {
  margin-top: 8px;
}

.templates-top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.templates-top-bar__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.templates-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background-color: #e3f2fd;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #1565c0;
}

.header__create-btn {
  width: auto;
  height: 46px;
}

.header__create-btn-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff;
  color: #0060fe;
}

.file-name-link {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: #1976d2;
  
  &:hover {
    text-decoration: underline;
  }
}

/* Стили модального окна */
.template-modal {
  min-width: 500px;
  max-width: 600px;
}

.template-modal__header {
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
}

.template-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex-grow: 1;
}

.template-modal__close {
  color: #666;
}

.template-modal__content {
  padding: 16px;
}

.template-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.template-form__section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.template-form__item {
  width: 100%;
}

.template-form__file-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.template-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

.template-modal__footer :deep(button) {
  width: auto;
  min-width: 100px;
  height: 36px;
}
</style>
